import { Component, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';

//services
import { PortalSettingsService } from '../../../core/services/portal-settings.service';
import { EmployeeInfoService } from '../../../core/services/employee-info.service';
import { AccountService } from '../services/account.service';

//guards
import { CanComponentDeactivate } from '../../../shared/guard/unsaved-changes.guard';

//modals
import { WarningModalComponent } from '../../../modals/shared/warning-modal/warning-modal.component';

@Component({
    selector: 'app-change-account-password',
    templateUrl: './change-account-password.component.html',
    styleUrls: ['./change-account-password.component.css'],
    standalone: false
})
export class ChangeAccountPasswordComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('changePasswordForm', { static: false }) changePasswordForm: NgForm;
  portalSettings: any;
  formData: any = {};
  passwordsMatch = true;
  backToUA = false;
  isIE: any;
  employeeDetails: any = {};
  employeeParams: any = {};
  securityPinMessage: string;
  logoHQCo: number;
  isResetPassword = false;
  logoUrl: string;
  alert: any = null;
  queryParams$: Subscription;
  navigation$: Subscription;
  isPasswordChanged = false;
  isFromPasswordModal = false;

  constructor(
    portalSettingsService: PortalSettingsService,
    private employeeInfoService: EmployeeInfoService,
    private accountService: AccountService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private renderer: Renderer2,
    private sharedModalService: BsModalService
  ) {
    this.portalSettings = portalSettingsService.getPortalSettings();
  }

  ngOnInit() {
    this.securityPinMessage = this.portalSettings.SecurityPinMessage.ConfigValue;
    this.logoHQCo = this.portalSettings.LogoHQCo.ConfigValue;
    this.isResetPassword = this.router.url.includes('resetaccountpassword');
    if (this.isResetPassword) {
      this.titleService.setTitle('Reset Account Password');
      this.renderer.setStyle(document.body, 'backgroundImage', null);
    }
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((params: any) => {
      this.changePasswordForm?.form?.markAsPristine();
      this.formData = {};
      this.passwordsMatch = true;
      this.backToUA = false;
      this.employeeDetails = {};
      this.employeeParams = {};
      this.alert = null;

      this.employeeParams.Token = params.token;
      this.employeeParams.KeyID = params.u;
      this.employeeParams.ManualToken = params.manualToken == 'true';
      this.employeeParams.Employee = params.employee;
      this.employeeParams.Company = params.company;
      this.employeeParams.UserKeyID = params.userKeyID;
      this.init();
    });

    this.navigation$ = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationStart && event.url.includes('changeaccountpassword') && this.isFromPasswordModal) {
        this.router.navigate(['/account/changeaccountpassword'], { queryParams: {}, fragment: '/UA=F/Reset=T' });
      }
    });
  }

  ngOnDestroy() {
    this.queryParams$.unsubscribe();
    this.navigation$.unsubscribe();
  }

  hasUnsavedChanges() {
    if (!this.isResetPassword && !this.isPasswordChanged && this.isFromPasswordModal) {
      const initialState = {
        modalData: {
          modalTitle: 'Password Change Required',
          modalMessage: 'You must change your password before proceeding.',
          closeButtonText: 'Ok',
        },
        backdrop: 'static',
        size: 'md',
      };
      this.sharedModalService.show(WarningModalComponent, { initialState, ignoreBackdropClick: true, keyboard: true });
      return false;
    }
    return true;
  }

  init() {
    if (!this.isResetPassword) {
      this.getRouteData();
      this.getEmployeeDetails();
    } else {
      this.setLogoUrl(this.logoHQCo, 'L');
    }
    this.isIE = this.detectIE();
  }

  confirmPassword() {
    this.passwordsMatch = this.validatePasswords();
  }

  clearErrors() {
    this.alert = null;
    this.passwordsMatch = true;
  }

  navigateToLogout() {
    window.location.href = '/Account/Logout';
  }

  changePassword() {
    if (!this.isResetPassword) {
      if (this.formData.newPassword == this.formData.confirmPassword) {
        this.accountService.changePassword(this.formData).then(
          (response: any) => {
            this.alert = { type: response.success ? 'success' : 'danger', msg: response.message };
            if (response.success) {
              this.isPasswordChanged = true;
              this.changePasswordForm?.form?.markAsPristine();
              this.formData = { employee: this.formData.employee, company: this.formData.company };
              //567043 - Successfully changed password message is not visible after resetting password
              if (this.employeeDetails.IsCurrentlyLoggedInUser) {
                // Mark employee info as stale and refetch to get updated data
                this.employeeInfoService.setEmployeeInfoIsStale();
                this.employeeInfoService.fetchEmployeeInfo().pipe(take(1)).subscribe();
              }
              setTimeout(() => {
                if (this.backToUA) {
                  this.router.navigate(['/admin/manageemployees']);
                } else {
                  this.router.navigate(['/']);
                }
              }, 1000);
            }
          },
          () => {
            this.alert = { type: 'danger', msg: 'Error occurred while changing password, contact administrator.' };
          }
        );
      }
    } else {
      if (!this.employeeParams.ManualToken) {
        this.accountService
          .resetPassword(
            this.employeeParams.KeyID,
            this.formData.securityPIN,
            this.employeeParams.Token,
            this.formData.newPassword,
            this.formData.confirmPassword
          )
          .then((response: any) => {
            if (response != null && response.success) {
              this.navigateToLogout();
            } else if (response != null && !response.success) {
              this.alert = { type: 'danger', msg: response.error };
            }
          });
      } else {
        this.accountService
          .manualResetPassword(this.formData.newPassword, this.formData.confirmPassword, this.formData.verificationPIN)
          .then((response: any) => {
            if (response != null && response.success) {
              this.navigateToLogout();
            } else if (response != null && !response.success) {
              this.alert = { type: 'danger', msg: response.error };
            }
          });
      }
    }
  }

  navigateToLogin() {
    this.router.navigate(['/account/login'], { queryParams: { returnUrl: '/' } });
  }

  private setLogoUrl(co: number, size = 'L') {
    this.logoUrl = '/Company/GetLogo?co=' + co + '&size=' + size;
  }

  private getRouteData() {
    const hash = window.location.hash;
    if (hash != null && hash.length > 1) {
      const routeRegex = /^#[/]UA[=](T|F)?(\/Reset[=](T|F))?$/;
      const routeMatch = routeRegex.exec(hash);
      if (routeMatch != null) {
        this.backToUA = routeMatch[1] == 'T';
        this.isFromPasswordModal = routeMatch[3] == 'T';
      }
    }
  }

  private detectIE() {
    const ua = window.navigator.userAgent;

    const msie = ua.indexOf('MSIE ');
    if (msie > 0) {
      // IE 10 or older => return version number
      return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }

    const trident = ua.indexOf('Trident/');
    if (trident > 0) {
      // IE 11 => return version number
      const rv = ua.indexOf('rv:');
      return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }

    const edge = ua.indexOf('Edge/');
    if (edge > 0) {
      // Edge (IE 12+) => return version number
      return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }

    // other browser
    return false;
  }

  private validatePasswords() {
    const arePasswordsFilled = this.formData.confirmPassword?.trim() != null && this.formData.newPassword?.trim() != null;
    const passwordsMatch = this.formData.confirmPassword == this.formData.newPassword && arePasswordsFilled;
    if (!passwordsMatch && arePasswordsFilled) {
      this.alert = { type: 'danger', msg: 'Passwords must match!' };
    }
    return passwordsMatch;
  }

  private getEmployeeDetails() {
    this.accountService
      .getEmployeeDetails(this.employeeParams.UserKeyID, this.employeeParams.Employee, this.employeeParams.Company)
      .then((response: any) => {
        if (response != null) {
          this.employeeDetails = response;
          this.formData.employee = response.EmployeeId;
          this.formData.company = response.CompanyId;
        }
      });
  }

  showCurrentPassword(): boolean {
    return (
      !this.employeeDetails.IsTIDLinked &&
      ((!this.employeeDetails.IsSystemAdmin && !this.employeeDetails.IsSecurityAdmin) || this.employeeDetails.IsCurrentlyLoggedInUser)
    );
  }
}
